import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useState } from "react";

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  navItems: NavItem[];
  title: string;
  subtitle?: string;
}

export default function DashboardLayout({ children, navItems, title, subtitle }: DashboardLayoutProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-soft-gray flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200 fixed inset-y-0 left-0 z-30">
        <div className="p-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="https://static.readdy.ai/image/2f07940fa5d3a90c9fe64cfd1f3be31d/a5100fd298f183af8516edaf12c4c388.png"
              alt="Sycamore Oasis"
              className="h-9 w-auto object-contain"
            />
            <div>
              <span className="text-sm font-semibold text-brand-green-800 leading-tight block">
                Sycamore Oasis
              </span>
              <span className="text-[10px] font-medium uppercase tracking-wider text-brand-green-600 block">
                {title}
              </span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-brand-green-50 text-brand-green-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} ${active ? "text-brand-green-600" : "text-gray-400"}`} />
                </div>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-brand-green-100 flex items-center justify-center shrink-0">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line text-brand-green-600" />
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.full_name || user?.email || "User"}
              </p>
              <p className="text-xs text-gray-400 capitalize truncate">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-r-line" />
            </div>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://static.readdy.ai/image/2f07940fa5d3a90c9fe64cfd1f3be31d/a5100fd298f183af8516edaf12c4c388.png"
              alt="Sycamore Oasis"
              className="h-8 w-auto object-contain"
            />
            <span className="text-sm font-semibold text-brand-green-800">Dashboard</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-50"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-3-line"} text-lg text-gray-700`} />
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
            <nav className="p-3 space-y-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? "bg-brand-green-50 text-brand-green-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} ${active ? "text-brand-green-600" : "text-gray-400"}`} />
                    </div>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-logout-box-r-line" />
                </div>
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        <div className="lg:hidden h-14" />
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {subtitle && (
            <div className="mb-6">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{subtitle}</h1>
              <p className="text-sm text-gray-500 mt-1">
                Welcome back, {user?.full_name?.split(" ")[0] || "there"}
              </p>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}